/**
 * Composant et utilitaires pour l'overlay "anniversaires" du lecteur d'overlays.
 * Responsabilité unique : afficher les prochains anniversaires (30 jours).
 */

import React from 'react';
import {
  MONTH_NAMES,
  UPCOMING_BIRTHDAY_DAYS,
  BIRTHDAY_EXIT_ANIMATION_MS,
} from './constants';

/**
 * Retourne la prochaine date d'anniversaire (cette année ou l'année suivante).
 * @param {number} month - Mois (1-12)
 * @param {number} day - Jour (1-31)
 * @returns {Date}
 */
export function getNextBirthdayDate(month, day) {
  const m = Math.max(1, Math.min(12, Number(month) || 1));
  const d = Math.max(1, Math.min(31, Number(day) || 1));
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  let next = new Date(today.getFullYear(), m - 1, d);
  if (next < today) next = new Date(today.getFullYear() + 1, m - 1, d);
  return next;
}

/**
 * Filtre et trie les anniversaires pour ne garder que les prochains (dans X jours).
 * @param {Array<{ username: string, month: number, day: number }>} birthdays
 * @returns {Array<{ username: string, month: number, day: number, nextDate: Date, daysUntil: number, dateLabel: string }>}
 */
export function getUpcomingCloseBirthdays(birthdays) {
  if (!birthdays || birthdays.length === 0) return [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const results = [];
  for (const b of birthdays) {
    const nextDate = getNextBirthdayDate(b.month, b.day);
    const daysUntil = Math.floor((nextDate - today) / (24 * 60 * 60 * 1000));
    if (daysUntil < 0 || daysUntil > UPCOMING_BIRTHDAY_DAYS) continue;
    const monthName = MONTH_NAMES[nextDate.getMonth()];
    const dateLabel = `${nextDate.getDate()} ${monthName}`;
    results.push({
      username: b.username || '',
      month: Number(b.month) || 1,
      day: Number(b.day) || 1,
      nextDate,
      daysUntil,
      dateLabel,
    });
  }
  results.sort((a, b) => a.daysUntil - b.daysUntil || a.nextDate - b.nextDate);
  return results;
}

/**
 * Libellé court pour "dans X jours" (aujourd'hui, demain, dans N jours).
 * @param {number} daysUntil
 * @returns {string}
 */
export function formatDaysUntil(daysUntil) {
  if (daysUntil === 0) return "Aujourd'hui";
  if (daysUntil === 1) return 'Demain';
  return `Dans ${daysUntil} jours`;
}

/** Réexport pour usage externe (tests, cohérence) */
export { BIRTHDAY_EXIT_ANIMATION_MS };

/**
 * Contenu overlay anniversaires : affiche uniquement les prochains (30 jours).
 * @param {{ birthdays: Array<{ username: string, month: number, day: number }>, exiting?: boolean }} props
 */
export function BirthdayOverlayContent({ birthdays, exiting = false }) {
  const upcoming = getUpcomingCloseBirthdays(birthdays);
  return (
    <div
      className={`overlayBirthday${exiting ? ' overlayBirthday--exiting' : ''}`}
    >
      <div className="overlayBirthdayHeader">
        <div className="overlayBirthdayHeaderInner">
          <span className="overlayBirthdayIcon" aria-hidden></span>
          <div className="overlayBirthdayTitleBlock">
            <span className="overlayBirthdayTitle">
              Prochains anniversaires
            </span>
            <span className="overlayBirthdaySubtitle">
              {UPCOMING_BIRTHDAY_DAYS} prochains jours
            </span>
          </div>
        </div>
        <div className="overlayBirthdayHeaderLine" />
      </div>
      <div className="overlayBirthdayList">
        {upcoming.length === 0 ? (
          <div className="overlayBirthdayEmpty">
            <span className="overlayBirthdayEmptyIcon" aria-hidden>
              ✨
            </span>
            <p>
              Aucun anniversaire dans les {UPCOMING_BIRTHDAY_DAYS} prochains
              jours
            </p>
          </div>
        ) : (
          upcoming.map((item, index) => (
            <div
              key={`${item.month}-${item.day}-${item.username}`}
              className={`overlayBirthdayCard ${
                item.daysUntil === 0
                  ? 'overlayBirthdayCard--today'
                  : item.daysUntil === 1
                  ? 'overlayBirthdayCard--tomorrow'
                  : ''
              }`}
              style={{ animationDelay: `${index * 55}ms` }}
            >
              <span className="overlayBirthdayCardDay" aria-hidden>
                {item.day}
              </span>
              <div className="overlayBirthdayCardBody">
                <span className="overlayBirthdayCardPseudo">
                  {item.username}
                </span>
                <span className="overlayBirthdayCardDate">
                  {item.dateLabel}
                </span>
              </div>
              <span className="overlayBirthdayCardBadge">
                {formatDaysUntil(item.daysUntil)}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
